import React, { useEffect, useMemo, useState } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import toast from "react-hot-toast";

const PUPIL_VOICE_FIELDS = [
  {
    key: "whatHelpsMe",
    label: "What helps me",
    placeholder: "Things adults, friends or strategies that help me learn and feel safe.",
  },
  {
    key: "whatIFindHard",
    label: "What I find hard",
    placeholder: "Situations or tasks I find tricky, things that make me feel worried or frustrated.",
  },
  {
    key: "myGoals",
    label: "My goals",
    placeholder: "What I want to get better at or be able to do this year.",
  },
];

function normalisePupilVoice(value) {
  if (!value || typeof value !== "object") {
    return { whatHelpsMe: "", whatIFindHard: "", myGoals: "" };
  }
  return {
    whatHelpsMe: value.whatHelpsMe || "",
    whatIFindHard: value.whatIFindHard || "",
    myGoals: value.myGoals || "",
  };
}

function normaliseParentVoice(value) {
  if (!value || typeof value !== "object") {
    return { aspirations: "", concerns: "" };
  }
  return {
    aspirations: value.aspirations || "",
    concerns: value.concerns || "",
  };
}

function formatTimestamp(value) {
  if (!value) return null;
  try {
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(date);
  } catch (_) {
    return null;
  }
}

export default function PupilParentVoicePanel({ student, onSaved }) {
  const initialPupilVoice = useMemo(() => normalisePupilVoice(student?.pupilVoice), [student?.pupilVoice]);
  const initialParentVoice = useMemo(() => normaliseParentVoice(student?.parentVoice), [student?.parentVoice]);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pupilVoice, setPupilVoice] = useState(initialPupilVoice);
  const [parentVoice, setParentVoice] = useState(initialParentVoice);

  useEffect(() => {
    setPupilVoice(initialPupilVoice);
    setParentVoice(initialParentVoice);
  }, [initialPupilVoice, initialParentVoice]);

  const hasAnyPupilVoice = PUPIL_VOICE_FIELDS.some((f) => initialPupilVoice[f.key]?.trim());
  const hasAnyParentVoice = (initialParentVoice.aspirations || initialParentVoice.concerns || "").trim().length > 0;
  const lastUpdated = formatTimestamp(student?.voiceUpdatedAt);

  const handleCancel = () => {
    setPupilVoice(initialPupilVoice);
    setParentVoice(initialParentVoice);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!student?.id) {
      toast.error("Cannot save: student id missing.");
      return;
    }
    try {
      setSaving(true);
      await updateDoc(doc(db, "students", student.id), {
        pupilVoice: {
          whatHelpsMe: pupilVoice.whatHelpsMe?.trim() || "",
          whatIFindHard: pupilVoice.whatIFindHard?.trim() || "",
          myGoals: pupilVoice.myGoals?.trim() || "",
        },
        parentVoice: {
          aspirations: parentVoice.aspirations?.trim() || "",
          concerns: parentVoice.concerns?.trim() || "",
        },
        voiceUpdatedAt: serverTimestamp(),
      });
      toast.success("Pupil and parent voice saved.");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      console.error("Error saving pupil/parent voice:", err);
      toast.error(err?.message || "Failed to save voice fields.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white dark:bg-gray-800 shadow rounded-lg">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base font-semibold text-gray-900 dark:text-white">
            Pupil &amp; Parent Voice
          </CardTitle>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Captured in the student&apos;s own words and from parent/carer conversations.
          </p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">Last updated {lastUpdated}</p>
          )}
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
          >
            {hasAnyPupilVoice || hasAnyParentVoice ? "Edit" : "Add"}
          </button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <section>
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">In the pupil&apos;s own words</h4>
          {editing ? (
            <div className="space-y-3">
              {PUPIL_VOICE_FIELDS.map((field) => (
                <label key={field.key} className="block">
                  <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{field.label}</span>
                  <textarea
                    rows={2}
                    value={pupilVoice[field.key] || ""}
                    onChange={(e) => setPupilVoice((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-900"
                  />
                </label>
              ))}
            </div>
          ) : hasAnyPupilVoice ? (
            <dl className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {PUPIL_VOICE_FIELDS.map((field) => (
                <div key={field.key} className="p-3 rounded-md bg-indigo-50/40 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800">
                  <dt className="text-xs font-semibold text-indigo-800 dark:text-indigo-200">{field.label}</dt>
                  <dd className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {initialPupilVoice[field.key]?.trim() || <span className="italic text-gray-400">Not yet captured</span>}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No pupil voice captured yet. Click &quot;Add&quot; to record what helps this child, what they find hard, and their own goals.
            </p>
          )}
        </section>

        <section>
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">From parent / carer</h4>
          {editing ? (
            <div className="space-y-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Aspirations</span>
                <textarea
                  rows={2}
                  value={parentVoice.aspirations || ""}
                  onChange={(e) => setParentVoice((prev) => ({ ...prev, aspirations: e.target.value }))}
                  placeholder="What does the family hope this child will achieve this year and longer term?"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-900"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Concerns</span>
                <textarea
                  rows={2}
                  value={parentVoice.concerns || ""}
                  onChange={(e) => setParentVoice((prev) => ({ ...prev, concerns: e.target.value }))}
                  placeholder="What worries the family right now? What support do they feel is missing?"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-900"
                />
              </label>
            </div>
          ) : hasAnyParentVoice ? (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 rounded-md bg-emerald-50/40 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800">
                <dt className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">Aspirations</dt>
                <dd className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {initialParentVoice.aspirations?.trim() || <span className="italic text-gray-400">Not yet captured</span>}
                </dd>
              </div>
              <div className="p-3 rounded-md bg-amber-50/40 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800">
                <dt className="text-xs font-semibold text-amber-800 dark:text-amber-200">Concerns</dt>
                <dd className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {initialParentVoice.concerns?.trim() || <span className="italic text-gray-400">Not yet captured</span>}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No parent or carer voice captured yet.
            </p>
          )}
        </section>

        {editing && (
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save voice"}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
